import { defineWidgetCore } from "../../widgetDefinition";
import { controllerSummary } from "../../Utils/controllerSummary";
import { resolveSubjectNames } from "../../Utils/subjectNames";
import { resolveDateRange } from "../../Utils/resolveDateRange";
import { WEEKDAY_TAGS, GAMEDAY_TAGS } from "../../AddWidgetModal/constants";
import { renderDynamicController } from "../../PDF/renderers/dynamicControllerPdfRenderer";
import type { DynamicControllerComponentSpec } from "../../Processing/types";

export const dynamicControllerCore = defineWidgetCore<DynamicControllerComponentSpec>({
  process(spec, response) {
    const data = response?.non_series_response;
    const engine = data && "type" in data && data.type === "slicer" ? data : undefined;
    return engine ? { type: "slicer", spec, engine } : { type: "slicer", spec };
  },
  pdf: {
    // The controller has no data, but a printed board is unreadable without knowing what
    // its widgets were scoped to — so the PDF carries the values it was set to.
    buildSnapshot({ processed, getSubjectEntities }) {
      if (processed.type !== "slicer") return null;
      const spec = processed.spec;
      const misc = spec.slicer_builder_misc ?? {};
      const controls = spec.controls ?? [];

      // The print reader cannot open the picker, so the rows carry the FULL selections —
      // "4 selected" on paper says nothing. controllerSummary stays for the on-screen
      // section headers, where it deliberately truncates.
      const rows: { label: string; value: string }[] = [];
      if (controls.includes("date")) {
        const dayLabels = (misc.dayFilter ?? []).map(
          (v) => [...WEEKDAY_TAGS, ...GAMEDAY_TAGS].find((t) => t.value === v)?.label ?? v,
        );
        rows.push({
          label: "Dates",
          value:
            resolveDateRange(misc) + (dayLabels.length ? ` · ${dayLabels.join(", ")}` : ""),
        });
      }
      if (controls.includes("single_date")) {
        rows.push({
          label: "Day",
          value: controllerSummary("single_date", misc, getSubjectEntities, controls),
        });
      }
      if (controls.includes("subject")) {
        const athletes = misc.selectedSubjects ?? [];
        const drills = misc.selectedDrills ?? [];
        const names = (subjects: string[], drillIds: string[]) =>
          getSubjectEntities
            ? resolveSubjectNames(subjects, drillIds, getSubjectEntities)
            : [...subjects, ...drillIds];
        rows.push({
          label: "Athletes",
          value: athletes.length ? names(athletes, []).join(", ") : "Full Team",
        });
        if (drills.length) {
          rows.push({ label: "Drills", value: names([], drills).join(", ") });
        }
      }
      if (controls.includes("single_player")) {
        rows.push({
          label: "Athlete",
          value: controllerSummary("single_player", misc, getSubjectEntities, controls),
        });
      }

      return {
        type: "slicer",
        title: misc.label || spec.display?.title || "Slicer",
        rows,
      };
    },
    render({ snapshot, rect, scale }) {
      if (snapshot.type !== "slicer") return null;
      return renderDynamicController(snapshot, rect, scale);
    },
  },
});
